// React
import React, { useMemo, memo } from 'react';
import i18next from 'i18next';

// Components
import { ChartGrid } from '@mobilitystore/charts-interface';
import Dropdown from '../../../msreact-kit/html-components/drop-down';
import Pagination from '../../../msreact-kit/html-components/table/pagination';
import { dropDownData } from '../../../msreact-kit/html-components/table/data';
import CustomCell from '../../../msreact-kit/html-components/custom-cell';

// Data
import { REGION } from '../../../data/idConstants';

// Assets
import { CDN_LINK } from '../../../assets/img';

// Css
import './index.scss';

const CustomIcon = ({ supplierContent, cellProps, onClick }) => {
  return (
    <div className='supplier-details__actions-cell'>
      <img
        id='edit'
        className='supplier-details__actions-cell__action-icon'
        src={`${CDN_LINK}/${supplierContent?.edit?.icon}`}
        alt={supplierContent?.edit?.alt}
        title={supplierContent?.edit?.alt}
        onClick={(event) =>
          onClick(event, cellProps?.data?.supplierId, cellProps?.data?.companyName)
        }
        tabIndex={0}
        role='button'
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ')
            onClick(
              event,
              cellProps?.data?.supplierId,
              cellProps?.data?.companyName
            );
        }}
      />

      <img
        id='view'
        className='supplier-details__actions-cell__action-icon'
        src={`${CDN_LINK}/${supplierContent?.view?.icon}`}
        alt={supplierContent?.view?.alt}
        title={supplierContent?.view?.alt}
        onClick={(event) =>
          onClick(event, cellProps?.data?.supplierId, cellProps?.data?.companyName)
        }
        tabIndex={0}
        role='button'
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ')
            onClick(
              event,
              cellProps?.data?.supplierId,
              cellProps?.data?.companyName
            );
        }}
      />
    </div>
  );
};

const SupplierDetailsGrid = memo(
  ({
    data,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onClick,
    content,
    totalPages
  }) => {
    const supplierContent = content?.supplier_details;
    const customIconRenderer = useMemo(
      () => (params) => (
        <CustomIcon
          supplierContent={supplierContent}
          cellProps={params}
          onClick={onClick}
        />
      ),
      [supplierContent, onClick]
    );
    const paginationValues = {
      totalPages: totalPages
    };

    const colDefs = useMemo(
      () => [
        {
          headerName: 'Supplier ID',
          field: 'supplierId',
          hide: true
        },
        {
          field: 'companyName',
          headerName: 'Company Name',
          cellRenderer: CustomCell,
          flex: 2
        },
        process.env.REACT_APP_REGION === REGION.IN.CODE
          ? { field: 'gst', headerName: 'GST', cellRenderer: CustomCell, flex: 1.5 }
          : { field: 'vat', headerName: 'VAT', cellRenderer: CustomCell, flex: 1.5 },
        {
          field: 'country',
          headerName: 'Country',
          cellRenderer: CustomCell,
          flex: 1
        },
        {
          field: 'actions',
          headerName: 'Actions',
          flex: 1,
          cellRenderer: customIconRenderer
        }
      ],
      [customIconRenderer]
    );

    const gridProps = useMemo(
      () => ({
        rowData: data,
        columnDefs: colDefs,
        suppressDragLeaveHidesColumns: true,
        filter: 'agTextColumnFilter',
        suppressMovableColumns: true,
        suppressLoadingOverlay: true,
        suppressScrollOnNewData: true
      }),
      [data, colDefs]
    );

    const handlePerPage = (event) => {
      onPageSizeChange(event.value.value);
      onPageChange(0);
    };

    const handlePagination = (number) => {
      onPageChange(number);
    };

    return (
      <>
        <div className='supplier-details__container'>
          <div className='supplier-details__table-wrapper'>
            <ChartGrid
              className='supplier-details__ag-table'
              gridProps={gridProps}
            />
          </div>
        </div>

        <div className='supplier-details__table'>
          <div className='supplier-details__table__container'>
            <div className='supplier-details__table__container__items'>
              <div className='supplier-details__table__container__items__title'>
                <p>
                  {i18next.t('ORDER_MANAGEMENT.ORDER_CARD.TABLE.ITEMS_PER_PAGE')} :
                </p>
              </div>
              <div className='supplier-details__table__container__items__drop'>
                <Dropdown
                  {...dropDownData}
                  handleChange={handlePerPage}
                  value={pageSize}
                />
              </div>
            </div>
            {paginationValues?.totalPages && (
              <div className='supplier-details__table__container__pagination'>
                <Pagination
                  className='pagination-bar'
                  currentPage={currentPage}
                  paginationData={paginationValues}
                  onPageChange={(page) => handlePagination(page)}
                />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
);

export default SupplierDetailsGrid;
